/**
 * BannersSection - Displays business promotional banners with same carousel layout as AssetsSection
 */
import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const BannersSection = ({ banners = [] }) => {
    const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    // Filter out expired banners
    const activeBanners = banners.filter(banner => {
        if (!banner.expiryDate) return true; // Show if no expiry date
        const expiryDate = new Date(banner.expiryDate);
        return expiryDate > new Date(); // Show only if expiry date is in the future
    });

    if (!activeBanners || activeBanners.length === 0) {
        return null;
    }

    const currentBanner = activeBanners[currentBannerIndex];
    const currentImages = (currentBanner?.images || []).filter(Boolean);

    const handleNextBanner = () => {
        setCurrentBannerIndex((prev) => (prev + 1) % activeBanners.length);
        setSelectedImageIndex(0);
    };

    const handlePrevBanner = () => {
        setCurrentBannerIndex((prev) => (prev === 0 ? activeBanners.length - 1 : prev - 1));
        setSelectedImageIndex(0);
    };

    const handleNextImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) => (prev + 1) % currentImages.length);
        }
    };

    const handlePrevImage = () => {
        if (currentImages.length > 0) {
            setSelectedImageIndex((prev) => (prev === 0 ? currentImages.length - 1 : prev - 1));
        }
    };

    const modalBanner = selectedBanner || currentBanner;
    const modalImages = (modalBanner?.images || []).filter(Boolean);

    return (
        <section className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-white/20">
            <h2 className="text-3xl font-bold text-white mb-6">Banners</h2>

            {/* Main Banner Carousel */}
            <div className="relative mb-8">
                <div className="relative h-80 bg-gray-800 rounded-lg overflow-hidden border border-white/20">
                    {currentImages.length > 0 ? (
                        <>
                            <img
                                src={currentImages[selectedImageIndex]}
                                alt={currentBanner.title}
                                className="w-full h-full object-cover"
                            />

                            {/* Navigation Controls */}
                            {currentImages.length > 1 && (
                                <>
                                    <button
                                        onClick={handlePrevImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                                    >
                                        <ChevronLeft className="w-6 h-6" />
                                    </button>
                                    <button
                                        onClick={handleNextImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                                    >
                                        <ChevronRight className="w-6 h-6" />
                                    </button>

                                    {/* Image Counter */}
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded text-sm text-white">
                                        {selectedImageIndex + 1} / {currentImages.length}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900">
                            <span className="text-gray-400">No images available</span>
                        </div>
                    )}
                </div>

                {/* Banner Info */}
                <div className="mt-4 p-4 bg-white/5 rounded-lg border border-white/20">
                    <div className="flex items-start justify-between mb-2">
                        <div>
                            <h3 className="text-xl font-semibold text-white">
                                {currentBanner.title}
                            </h3>
                            {currentBanner.subTitle && (
                                <p className="text-green-400 text-sm font-medium mt-1">
                                    {currentBanner.subTitle}
                                </p>
                            )}
                        </div>
                        {currentBanner.expiryDate && (
                            <div className="text-right text-xs text-gray-400">
                                <p>Expires</p>
                                <p className="font-semibold text-gray-300">
                                    {new Date(currentBanner.expiryDate).toLocaleDateString()}
                                </p>
                            </div>
                        )}
                    </div>
                    {currentBanner.description && (
                        <p className="text-gray-400 text-sm line-clamp-2">
                            {currentBanner.description}
                        </p>
                    )}
                </div>

                {/* Banner Navigation */}
                {activeBanners.length > 1 && (
                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={handlePrevBanner}
                            className="flex-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 font-semibold py-2 px-4 rounded-lg border border-gray-500/30 transition-all flex items-center justify-center gap-2"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </button>
                        <button
                            onClick={() => setSelectedBanner(currentBanner)}
                            className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-300 font-semibold py-2 px-4 rounded-lg border border-green-500/30 transition-all"
                        >
                            View Details
                        </button>
                        <button
                            onClick={handleNextBanner}
                            className="flex-1 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 font-semibold py-2 px-4 rounded-lg border border-gray-500/30 transition-all flex items-center justify-center gap-2"
                        >
                            Next
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Banner Indicators */}
                {activeBanners.length > 1 && (
                    <div className="flex gap-2 mt-4 justify-center flex-wrap">
                        {activeBanners.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => {
                                    setCurrentBannerIndex(index);
                                    setSelectedImageIndex(0);
                                }}
                                className={`h-2 rounded-full transition-all ${
                                    index === currentBannerIndex
                                        ? 'bg-green-400 w-8'
                                        : 'bg-white/30 hover:bg-white/50 w-2'
                                }`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Banner Detail Modal */}
            {selectedBanner && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-white/20 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
                        {/* Close Button */}
                        <button
                            onClick={() => setSelectedBanner(null)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Banner Title */}
                        <div className="mb-6 pr-8">
                            <h3 className="text-3xl font-bold text-white mb-2">
                                {selectedBanner.title}
                            </h3>
                            {selectedBanner.subTitle && (
                                <p className="text-green-400 text-lg font-medium">
                                    {selectedBanner.subTitle}
                                </p>
                            )}
                        </div>

                        {/* Image Gallery */}
                        {modalImages.length > 0 && (
                            <div className="relative mb-6">
                                <div className="relative h-96 bg-gray-800 rounded-lg overflow-hidden border border-white/20">
                                    <img
                                        src={modalImages[selectedImageIndex]}
                                        alt={`${selectedBanner.title} - Image ${selectedImageIndex + 1}`}
                                        className="w-full h-full object-contain"
                                    />

                                    {/* Navigation Controls */}
                                    {modalImages.length > 1 && (
                                        <>
                                            <button
                                                onClick={handlePrevImage}
                                                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                                            >
                                                <ChevronLeft className="w-6 h-6" />
                                            </button>
                                            <button
                                                onClick={handleNextImage}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all"
                                            >
                                                <ChevronRight className="w-6 h-6" />
                                            </button>

                                            {/* Image Counter */}
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm px-3 py-1 rounded text-sm text-white">
                                                {selectedImageIndex + 1} / {modalImages.length}
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Thumbnail Strip */}
                                {modalImages.length > 1 && (
                                    <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                                        {modalImages.map((img, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => setSelectedImageIndex(idx)}
                                                className={`flex-shrink-0 h-16 w-16 rounded-lg overflow-hidden border-2 transition-all ${
                                                    idx === selectedImageIndex
                                                        ? 'border-green-400'
                                                        : 'border-white/20 hover:border-white/50'
                                                }`}
                                            >
                                                <img
                                                    src={img}
                                                    alt={`Thumbnail ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Banner Details */}
                        <div className="space-y-4 mb-6">
                            <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                                <h4 className="text-gray-300 font-semibold mb-2">Description</h4>
                                <p className="text-gray-400 whitespace-pre-wrap break-words overflow-hidden">
                                    {selectedBanner.description || 'No description available'}
                                </p>
                            </div>

                            {selectedBanner.expiryDate && (
                                <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                                    <h4 className="text-gray-300 font-semibold mb-1">Expiry Date</h4>
                                    <p className="text-gray-400">
                                        {new Date(selectedBanner.expiryDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            )}

                            {selectedBanner.type && (
                                <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                                    <h4 className="text-gray-300 font-semibold mb-1">Banner Type</h4>
                                    <p className="text-gray-400 capitalize">
                                        {selectedBanner.type.replace(/_/g, ' ')}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={() => setSelectedBanner(null)}
                            className="w-full bg-green-500/20 hover:bg-green-500/30 text-green-300 font-semibold py-2 px-4 rounded-lg border border-green-500/30 transition-all"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default BannersSection;
